import { ReactElement } from 'react';
import { useEntitlement } from '../hooks/useEntitlement';
import { GatedControl } from './GatedControl';

interface EntitlementGateProps {
  /** Module/action to check via useEntitlement -- see api/libs/constants/package-entitlements.ts for what's actually gated per plan. */
  module: string;
  action: string;
  /** Shown in the upgrade tooltip when not entitled; falls back to UpgradeTooltip's default. */
  message?: string;
  /** A single control (Button, DropdownMenuItem, ...) that accepts `disabled` and `onClick` props. */
  children: ReactElement<{ disabled?: boolean; onClick?: (...args: any[]) => void }>;
}

/**
 * The plan-gated counterpart to `<RequirePermission>` (which gates on role):
 * renders `children` disabled and wrapped in an upgrade tooltip when the
 * active workgroup's plan doesn't entitle `module`/`action`, otherwise
 * renders them untouched. A thin `GatedControl` wrapper for the common
 * "boolean membership check" case -- see ComponentsHub.tsx's export button
 * and NewScan.tsx's custom-policy save/delete buttons for reference usage.
 * For quantity-limit gating (e.g. team-member seat caps), use
 * `GatedControl` directly with the limit-check result instead.
 */
export function EntitlementGate({ module, action, message, children }: EntitlementGateProps) {
  const entitled = useEntitlement(module, action);
  return (
    <GatedControl allowed={entitled} message={message}>
      {children}
    </GatedControl>
  );
}

export default EntitlementGate;
